import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Sparkles, RotateCw } from "lucide-react";
import { api } from "../../../lib/api";
import ArticleBody from "../../../components/shared/ArticleBody";

export default function AdvantageSeriesPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | not-found | error
  const [nonce, setNonce] = useState(0);

  const fetchPost = useCallback((signal) => {
    setStatus("loading");
    setPost(null);
    return api
      .get(`/api/workspace/pro-carer-circle/advantage-series/${slug}`, { signal })
      .then((r) => {
        setPost(r.data);
        setStatus("ok");
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        setStatus(err?.response?.status === 404 ? "not-found" : "error");
      });
  }, [slug]);

  useEffect(() => {
    const ctrl = new AbortController();
    fetchPost(ctrl.signal);
    return () => ctrl.abort();
  }, [fetchPost, nonce]);

  const backLink = (
    <Link
      to="/workspace/pro-carer-circle/advantage-series"
      className="btn-outline !px-4 !py-2 text-sm"
      data-testid="advantage-series-post-back"
    >
      <ArrowLeft className="w-4 h-4" /> Back to the Advantage Series
    </Link>
  );

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-white py-16 text-center text-ink-muted" data-testid="page-advantage-series-post">
        Loading article…
      </div>
    );
  }

  if (status === "not-found" || status === "error") {
    return (
      <div className="min-h-screen bg-white" data-testid="page-advantage-series-post">
        <div className="container-jcc max-w-3xl pt-8 md:pt-12 text-center py-16">
          <p className="text-ink-muted mb-6">
            {status === "not-found" ? "We couldn't find that article." : "Something went wrong loading this article."}
          </p>
          <div className="flex items-center justify-center gap-3">
            {status === "error" && (
              <button
                type="button"
                onClick={() => setNonce((n) => n + 1)}
                className="btn-outline inline-flex items-center gap-2"
                data-testid="advantage-series-post-retry"
              >
                <RotateCw className="w-4 h-4" /> Try again
              </button>
            )}
            {backLink}
          </div>
        </div>
      </div>
    );
  }

  return (
    <article className="bg-white" data-testid="page-advantage-series-post">
      <div className="container-jcc pt-8 md:pt-12 max-w-3xl">{backLink}</div>

      <header className="container-jcc max-w-3xl pt-6 pb-2">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="pill bg-amber-50 text-amber-800 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> {post.category || "Advantage Series"}
          </span>
          {post.read_minutes ? (
            <span className="text-sm text-ink-soft flex items-center gap-1">
              <Clock className="w-4 h-4" /> {post.read_minutes} min read
            </span>
          ) : null}
          {post.author ? <span className="text-sm text-ink-soft">· {post.author}</span> : null}
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold leading-[1.1]">
          {post.title}
        </h1>
        {post.excerpt && (
          <p className="mt-4 text-lg text-ink-muted leading-relaxed">{post.excerpt}</p>
        )}
      </header>

      {post.featured_image && (
        <div className="container-jcc max-w-4xl mt-8">
          <div className="aspect-[16/7] rounded-3xl overflow-hidden">
            <img src={post.featured_image} alt="" className="w-full h-full object-cover" />
          </div>
        </div>
      )}

      <div className="container-jcc max-w-3xl mt-10 text-ink pb-20">
        <ArticleBody body={post.body} testId="advantage-series-post-body" />
      </div>

      <div className="container-jcc max-w-3xl pb-16">{backLink}</div>
    </article>
  );
}
